import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useProcessingOverlayStore } from '@/stores/processingOverlayStore';
import { announceBackgroundTaskStarted } from '@/lib/announceBackgroundTask';
import { useShallow } from 'zustand/react/shallow';
import { AgentProcessingOverlay } from './AgentProcessingOverlay';

export const ProcessingOverlayRoot: React.FC = () => {
  const { isVisible, isMinimized, status, config, error, progress, currentItem, totalItems, message } =
    useProcessingOverlayStore(useShallow((s) => ({
      isVisible: s.isVisible,
      isMinimized: s.isMinimized,
      status: s.status,
      config: s.config,
      error: s.error,
      progress: s.progress,
      currentItem: s.currentItem,
      totalItems: s.totalItems,
      message: s.message,
    })));

  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMinimize = useCallback(() => {
    const { config: cfg } = useProcessingOverlayStore.getState();
    const rect = cardRef.current?.getBoundingClientRect();
    const centerX = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
    const centerY = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;

    announceBackgroundTaskStarted(cfg?.title || t('backgroundTask.processing'), {
      x: centerX,
      y: centerY,
    });
    if (cfg?.onMinimize) {
      cfg.onMinimize();
      useProcessingOverlayStore.getState().close();
    } else {
      useProcessingOverlayStore.getState().minimize();
    }
  }, [t]);

  const handleClose = useCallback(() => {
    const { config: cfg, close } = useProcessingOverlayStore.getState();
    cfg?.onClose?.();
    close();
  }, []);

  const handleRetry = useCallback(() => {
    useProcessingOverlayStore.getState().config?.onRetry?.();
  }, []);

  if (!isVisible || isMinimized) return null;

  return (
    <div ref={cardRef}>
      <AgentProcessingOverlay
        isVisible={true}
        status={status}
        error={error}
        onRetry={config?.onRetry ? handleRetry : undefined}
        onClose={handleClose}
        onMinimize={handleMinimize}
        minimizable={true}
        title={config?.title || ''}
        description={config?.description}
        message={message}
        processingMessage={config?.processingMessage}
        errorMessage={config?.errorMessage}
        variant="dialog"
        showProgress={config?.showProgress}
        progress={progress}
        currentItem={currentItem}
        totalItems={totalItems}
      />
    </div>
  );
};
